import {useEffect} from 'react';

import {assert} from 'Asserts';
import {ProcedureMetadata, ProcedureWithMetadata} from 'procedures/ProcedureDomainTypes';
import {PersistenceInfo} from 'procedures/ProcedurePageContext';
import AutosaveManager from 'procedures/redux/autosaveManager';
import {
  procedureDirtyStateChanged,
  procedureIsSaveInFlightStateChanged,
  ProceduresAction,
} from 'procedures/redux/proceduresActions';
import {SingleProcedureReduxData} from 'procedures/redux/reduceProcedures';
import {getStore} from 'redux-stores/Store';
import useOnMount from 'ui-library/hooks/useOnMount';

function getAllReduxData(itemId: string): SingleProcedureReduxData | undefined {
  return getStore().getState().models.procedures[itemId];
}
function isDirty(itemId: string): boolean {
  return Boolean(getAllReduxData(itemId)?.liveProperties.isDirty);
}

function isReadOnly(itemId: string): boolean {
  return Boolean(getAllReduxData(itemId)?.liveProperties.isReadOnly);
}

/** @package */
export function shouldSaveNow(itemId: string): boolean {
  const modelData = getAllReduxData(itemId);
  if (modelData != null) {
    const {liveProperties} = modelData;

    return (
      liveProperties.isDirty &&
      !liveProperties.isReadOnly &&
      !liveProperties.isAutosaverPaused &&
      !liveProperties.isAutosaverBlocked
    );
  } else {
    return false;
  }
}

/** @package */
export function getDataToSave(itemId: string): ProcedureWithMetadata {
  const data = getAllReduxData(itemId);
  assert(data != null, 'Autosaver is not initialized.', {itemId});
  assert(data.procedureMetadata != null, 'Procedure metadata is not initialized.', {itemId});
  return {
    flowchart: data.flowchart,
    metadata: data.procedureMetadata,
    processFlowchartConfigId: null,
  };
}

function emitAction(action: ProceduresAction): void {
  getStore().dispatch(action);
}

function setIsSaving(itemId: string, isSaveInFlight: boolean): void {
  emitAction(procedureIsSaveInFlightStateChanged(itemId, isSaveInFlight));
}

const callbacks = {
  isDirty,
  isReadOnly,
  shouldSaveNow,
  getDataToSave,
  setIsSaving,
};

/**
 * This hook is a thin wrapper around the AutosaveManager, customized for procedures. It needs to be
 * used in a component that owns the `save` API call.
 *
 * Additional Redux-management tools are provided by ProcedureFlowchartStateManager.
 */
export default function useProceduresAutosaveManager(
  itemId: string,
  doSave: (
    procedure: ProcedureWithMetadata,
    isBlocking: boolean,
    saveContext?: PersistenceInfo
  ) => Promise<ProcedureMetadata>
): void {
  useOnMount(() => {
    // This ensures that the Redux data is initialized before the autosaver starts
    emitAction(procedureDirtyStateChanged(itemId, false));

    AutosaveManager.initialize(itemId, {...callbacks, doSave});

    return function cleanup() {
      AutosaveManager.destroy(itemId);
    };
  });

  useEffect(() => {
    AutosaveManager.updateSaveHandler(itemId, doSave);
  }, [itemId, doSave]);
}
